import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

interface AuthUser {
  email: string;
  firstName: string;
  first_name: string;
  lastName: string;
  last_name: string;
  name: string;
  id: string;
  userType: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthUser | null;
  login: (token: string, userData: any) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  login: () => {},
  logout: () => {},
});

export const useAuthContext = () => useContext(AuthContext);

// Casting owns sign-in, so this re-reads the shared .heyjoe.io cookie. Coming back to
// the tab (from casting.heyjoe.io) re-checks at once; the timer only catches a sign-out
// in a tab that stays open, so it runs slowly and never while the tab is hidden.
// It ran every 10s in every tab until Oct 21: half of all Plus logs, and a casting
// /verify-token call each time.
const POLL_INTERVAL = 5 * 60_000;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const pollRef = useRef<number | null>(null);

  const checkAuth = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/validate', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const next = normalizeUser(data);
        const nextJson = JSON.stringify(next);
        if (data.token && localStorage.getItem('authToken') !== data.token) localStorage.setItem('authToken', data.token);
        if (localStorage.getItem('castingUser') !== nextJson) localStorage.setItem('castingUser', nextJson);
        // Same person as before: keep the same object so nothing downstream re-renders.
        setUser(prev => (prev && JSON.stringify(prev) === nextJson ? prev : next));
      } else {
        setUser(null);
      }
    } catch {
      // Network error — keep current state
    }
  }, []);

  // Handle ?token= URL handoff from casting.heyjoe.io (Google OAuth redirect)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    if (!urlToken) return;

    // Clean token from URL immediately
    params.delete('token');
    const clean = params.toString()
      ? `${window.location.pathname}?${params}`
      : window.location.pathname;
    window.history.replaceState({}, '', clean);

    // Validate the token — this also sets the cookie server-side
    (async () => {
      try {
        const res = await fetch('/api/auth/check-casting', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: urlToken }),
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          setUser(normalizeUser(data.user || data));
        }
      } catch { /* ignore */ }
      setIsLoading(false);
    })();
  }, []);

  // Poll for auth state — picks up .heyjoe.io cookie from casting login
  useEffect(() => {
    // Initial check
    checkAuth().then(() => setIsLoading(false));

    // Poll on interval
    pollRef.current = window.setInterval(() => {
      if (document.visibilityState === 'visible') checkAuth();
    }, POLL_INTERVAL);

    // Also check when tab regains focus (user coming back from casting.heyjoe.io)
    const onVisible = () => {
      if (document.visibilityState === 'visible') checkAuth();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [checkAuth]);

  const login = useCallback((token: string, userData: any) => {
    // Persist token so API calls (useCredits, useSubscription, etc.) can read it
    localStorage.setItem('authToken', token);
    localStorage.setItem('castingUser', JSON.stringify(normalizeUser(userData)));
    setUser(normalizeUser(userData));
  }, []);

  const logout = useCallback(() => {
    // Clear cookies across all domain variants
    const domains = ['', '.heyjoe.io', 'heyjoe.io', 'localhost'];
    const cookieNames = ['token', 'castingAuthToken', 'authToken'];
    domains.forEach(domain => {
      cookieNames.forEach(name => {
        document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC${domain ? `; domain=${domain}` : ''}`;
      });
    });

    setUser(null);
    window.location.href = '/';
  }, []);

  return (
    <AuthContext.Provider value={{
      isAuthenticated: !!user,
      isLoading,
      user,
      login,
      logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

function normalizeUser(data: any): AuthUser {
  const firstName = (data.first_name || data.firstName || '').trim();
  const lastName = (data.last_name || data.lastName || '').trim();
  return {
    email: data.email || '',
    firstName,
    first_name: firstName,
    lastName,
    last_name: lastName,
    name: data.name || `${firstName} ${lastName}`.trim(),
    id: data._id || data.id || '',
    userType: data.user_type || data.userType || 'user',
  };
}
